// Inline step list for a personal task, expanded directly under its row in the
// List overview (TaskListView). Lazy-loads the task's steps on first render,
// then lets the user toggle / add / delete them without opening the detail
// modal. Mirrors the DetailPanel "Schritte" UI in a compact, indented form.
//
// Keeps its own local step state for snappy optimistic updates, and calls
// refreshPersonalTasks() after each mutation so the row's step-count badge (fed by the
// list payload's step counts) stays in sync.

import { useEffect, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import {
  CalendarPlus,
  Check,
  Circle,
  Flag,
  Link2,
  Plus,
  Trash2,
  ArrowRightToLine,
  ArrowUpRight,
} from "lucide-preact";
import type { PersonalTaskStep } from "@juuplan/shared";
import {
  addLinkedStep,
  addStep,
  deleteStep,
  fetchSteps,
  promoteStep,
  refreshPersonalTasks,
  updateStep,
} from "../store/personalTasks";
import { api } from "../lib/api";
import { formatDateShort, localTodayIso, TASK_PRIORITY_LABEL, TASK_PRIORITY_VAR, softPillStyle } from "../lib/format";
import { toastError, toastSuccess } from "../lib/toast";
import { openPersonalTaskSig, openProjectTaskSig } from "../store/deepLinks";
import { carryOverStepsSig } from "../store/carryOverSteps";
import { AvatarStack } from "./AvatarStack";
import { personalAssigneeIds } from "../lib/personalAssignees";

interface LinkSearchResult {
  type: "task" | "personal_task";
  id: number;
  title: string;
  subtitle?: string;
}

interface Props {
  taskId: number;
  readOnly?: boolean;
}

export function RecordStepList(props: Props): preact.JSX.Element {
  const { taskId, readOnly = false } = props;
  const [, setLocation] = useLocation();
  const [steps, setSteps] = useState<PersonalTaskStep[] | null>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  // Aufgabe-verknüpfen-Modus: kleines Suchfeld statt Titel-Eingabe.
  const [linkMode, setLinkMode] = useState(false);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState<LinkSearchResult[] | null>(null);
  const linkSearchSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void fetchSteps(taskId).then(
      (rows) => {
        if (!cancelled) setSteps(rows ?? []);
      },
      () => {
        if (!cancelled) setSteps([]);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  // Verknüpfungs-Suche: debounced /api/search über Projekt- + persönliche
  // Aufgaben; bereits verknüpfte Ziele und die Eltern-Aufgabe selbst werden
  // ausgefiltert. `status=open` blendet erledigte/abgebrochene Aufgaben aus —
  // ein abgeschlossenes Ziel ist als Schritt sinnlos.
  useEffect(() => {
    if (!linkMode) return;
    const q = linkQuery.trim();
    if (q.length < 2) {
      setLinkResults(null);
      return;
    }
    const seq = ++linkSearchSeq.current;
    const t = setTimeout(() => {
      void api<{ results: LinkSearchResult[] }>(
        `/api/search?q=${encodeURIComponent(q)}&type=task,personal_task&status=open`
      ).then(
        (data) => {
          if (seq !== linkSearchSeq.current) return;
          const linkedIds = new Set(
            (steps ?? [])
              .filter((s) => s.linked_info)
              .map((s) => `${s.linked_info!.type}:${s.linked_info!.id}`)
          );
          setLinkResults(
            data.results
              .filter((r) => r.type === "task" || r.type === "personal_task")
              .filter((r) => !(r.type === "personal_task" && r.id === taskId))
              .filter((r) => !linkedIds.has(`${r.type}:${r.id}`))
              .slice(0, 8)
          );
        },
        () => {
          if (seq === linkSearchSeq.current) setLinkResults([]);
        }
      );
    }, 250);
    return () => clearTimeout(t);
  }, [linkMode, linkQuery, steps, taskId]);

  const link = async (result: LinkSearchResult): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const created = await addLinkedStep(
        taskId,
        result.type === "task"
          ? { linked_task_id: result.id }
          : { linked_personal_task_id: result.id }
      );
      setSteps((prev) => [...(prev ?? []), created]);
      setLinkQuery("");
      setLinkResults(null);
      setLinkMode(false);
      void refreshPersonalTasks();
    } catch {
      toastError("Aufgabe konnte nicht verknüpft werden");
    } finally {
      setBusy(false);
    }
  };

  const openLinked = (step: PersonalTaskStep): void => {
    const info = step.linked_info;
    if (!info) return;
    if (!info.access) return;
    if (info.type === "task") {
      // Signal + Navigation, wie in der Command-Palette: das Signal öffnet das
      // Detail, sobald die Projekt-Ansicht geladen ist.
      openProjectTaskSig.value = info.id;
      if (info.project_id != null) setLocation(`/p/${info.project_id}`);
    } else {
      openPersonalTaskSig.value = info.id;
    }
  };

  const toggle = async (step: PersonalTaskStep): Promise<void> => {
    // Referenz auf eine Projektaufgabe: Status gehört dem Projekt-Workflow und
    // ist hier read-only. Ohne Zugriff aufs Ziel ebenfalls kein Toggle.
    if (step.linked_info && (step.linked_info.type === "task" || !step.linked_info.access)) {
      return;
    }
    // optimistic
    setSteps((prev) =>
      (prev ?? []).map((s) => (s.id === step.id ? { ...s, is_done: !s.is_done } : s))
    );
    try {
      await updateStep(step.id, { is_done: !step.is_done });
      void refreshPersonalTasks();
    } catch {
      setSteps((prev) =>
        (prev ?? []).map((s) => (s.id === step.id ? { ...s, is_done: step.is_done } : s))
      );
      toastError("Änderung fehlgeschlagen");
    }
  };

  const setDue = async (step: PersonalTaskStep, due: string | null): Promise<void> => {
    // optimistic
    setSteps((prev) =>
      (prev ?? []).map((s) => (s.id === step.id ? { ...s, due_date: due } : s))
    );
    try {
      await updateStep(step.id, { due_date: due });
      void refreshPersonalTasks();
    } catch {
      setSteps((prev) =>
        (prev ?? []).map((s) => (s.id === step.id ? { ...s, due_date: step.due_date } : s))
      );
      toastError("Fälligkeit konnte nicht gespeichert werden");
    }
  };

  const startEdit = (step: PersonalTaskStep): void => {
    if (readOnly) return;
    // Referenz-Schritte spiegeln den Titel ihres Ziels — nicht editierbar.
    if (step.linked_info) return;
    setEditingId(step.id);
    setEditValue(step.title);
  };

  const commitEdit = async (step: PersonalTaskStep): Promise<void> => {
    const trimmed = editValue.trim();
    setEditingId(null);
    if (!trimmed || trimmed === step.title) return;
    // optimistic
    setSteps((prev) =>
      (prev ?? []).map((s) => (s.id === step.id ? { ...s, title: trimmed } : s))
    );
    try {
      await updateStep(step.id, { title: trimmed });
      void refreshPersonalTasks();
    } catch {
      setSteps((prev) =>
        (prev ?? []).map((s) => (s.id === step.id ? { ...s, title: step.title } : s))
      );
      toastError("Umbenennen fehlgeschlagen");
    }
  };

  // Aus dem Schritt wird eine eigenständige Aufgabe DERSELBEN Liste. Verschieben,
  // nicht kopieren: die Zeile verlässt diese Checkliste — deshalb fliegt sie hier
  // sofort raus, und refreshPersonalTasks (in promoteStep) holt sie als Zeile der
  // Liste zurück.
  const promote = async (step: PersonalTaskStep): Promise<void> => {
    if (busy) return;
    const prev = steps ?? [];
    setBusy(true);
    setSteps(prev.filter((s) => s.id !== step.id));
    try {
      await promoteStep(step.id);
      toastSuccess(`„${step.title}" ist jetzt eine eigene Aufgabe`);
    } catch {
      // api() hat den Fehler bereits getoastet — hier nur die Zeile zurück.
      setSteps(prev);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (step: PersonalTaskStep): Promise<void> => {
    const prev = steps ?? [];
    setSteps(prev.filter((s) => s.id !== step.id));
    try {
      await deleteStep(step.id);
      void refreshPersonalTasks();
    } catch {
      setSteps(prev);
      toastError("Löschen fehlgeschlagen");
    }
  };

  const add = async (): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setTitle("");
    try {
      const created = await addStep(taskId, trimmed);
      setSteps((prev) => [...(prev ?? []), created]);
      void refreshPersonalTasks();
    } catch {
      // Restore the typed title so the input isn't lost on a failed request.
      setTitle(trimmed);
      toastError("Schritt konnte nicht hinzugefügt werden");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="border-b border-[color:var(--color-outline-variant)]/60 bg-[color:var(--color-surface-container-low)]/40 pl-11 pr-3 py-1.5">
      {steps === null ? (
        <p class="py-1 text-xs italic text-[color:var(--color-on-surface-variant)]">Schritte laden…</p>
      ) : (
        <ul class="flex flex-col">
          {steps.map((step) => {
            const linked = step.linked_info ?? null;
            const toggleLocked =
              readOnly || (linked !== null && (linked.type === "task" || !linked.access));
            return (
            <li
              key={step.id}
              class="group/step flex min-h-[36px] items-center gap-2.5 rounded-md px-1 py-1 hover:bg-[color:var(--color-surface-container-low)]"
            >
              <button
                type="button"
                disabled={toggleLocked}
                onClick={(e) => {
                  e.stopPropagation();
                  void toggle(step);
                }}
                class={`shrink-0 inline-flex size-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${toggleLocked ? "cursor-default opacity-60" : ""}`}
                aria-label={step.is_done ? `„${step.title}" wieder öffnen` : `„${step.title}" erledigen`}
                aria-pressed={step.is_done}
                title={
                  linked?.type === "task"
                    ? "Status wird von der Projektaufgabe übernommen"
                    : undefined
                }
              >
                {step.is_done ? (
                  <span class="inline-flex size-4 items-center justify-center rounded-full bg-[color:var(--color-status-done)] text-[color:var(--color-on-primary)]">
                    <Check size={14} aria-hidden="true" />
                  </span>
                ) : (
                  <Circle size={16} class="text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
                )}
              </button>
              {/* Verknüpfungs-Marker für Referenz-Schritte */}
              {linked && (
                <Link2
                  size={14}
                  class="shrink-0 text-[color:var(--color-primary)]"
                  aria-label={
                    linked.type === "task"
                      ? "Verknüpfte Projektaufgabe"
                      : "Verknüpfte persönliche Aufgabe"
                  }
                />
              )}
              {/* priority marker — read-only, only shown when non-default */}
              {step.priority !== "medium" && (
                <Flag
                  size={14}
                  class="shrink-0"
                  fill="currentColor"
                  style={{ color: TASK_PRIORITY_VAR[step.priority] }}
                  aria-label={`Priorität: ${TASK_PRIORITY_LABEL[step.priority]}`}
                />
              )}
              {/* assignee marker — read-only; seit #854 mehrere möglich */}
              {personalAssigneeIds(step).length > 0 && (
                <AvatarStack userIds={personalAssigneeIds(step)} />
              )}
              {editingId === step.id ? (
                <input
                  type="text"
                  value={editValue}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                  onBlur={() => void commitEdit(step)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setEditingId(null);
                    }
                  }}
                  maxLength={500}
                  aria-label={`„${step.title}" umbenennen`}
                  class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-1.5 py-0.5 text-base sm:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                />
              ) : (
                // <button> statt <span>: vorher liess sich ein Schritt nur
                // mit der Maus oeffnen (Klick) oder umbenennen (Doppelklick).
                // Jetzt: Enter/Leertaste oeffnet, F2 benennt um.
                <button
                  type="button"
                  aria-disabled={linked && !linked.access ? "true" : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (linked) {
                      openLinked(step);
                    } else {
                      openPersonalTaskSig.value = step.id;
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "F2") return;
                    e.preventDefault();
                    e.stopPropagation();
                    startEdit(step);
                  }}
                  onDblClick={(e) => {
                    e.stopPropagation();
                    startEdit(step);
                  }}
                  title={
                    linked
                      ? linked.access
                        ? "Verknüpfte Aufgabe öffnen"
                        : undefined
                      : readOnly
                        ? "Öffnen"
                        : "Klick zum Öffnen, Doppelklick zum Bearbeiten"
                  }
                  class={`flex-1 min-w-0 truncate rounded text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                    linked && !linked.access ? "cursor-default" : "cursor-pointer"
                  } ${
                    step.is_done ? "line-through opacity-60" : "text-[color:var(--color-on-surface)]"
                  }`}
                >
                  {step.title}
                  {linked?.trashed && (
                    <span class="ml-1.5 text-micro italic text-[color:var(--color-on-surface-variant)]">
                      (gelöscht)
                    </span>
                  )}
                  {linked && !linked.access && !linked.trashed && (
                    <span class="ml-1.5 text-micro italic text-[color:var(--color-on-surface-variant)]">
                      (kein Zugriff)
                    </span>
                  )}
                </button>
              )}
              {/* Live-Status des verknüpften Ziels */}
              {linked?.access && step.status_info && (
                <span
                  class="shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-label font-medium"
                  style={softPillStyle(step.status_info.color)}
                  title={`Status: ${step.status_info.name}`}
                >
                  <span
                    class="inline-block size-1.5 rounded-full"
                    style={{ backgroundColor: step.status_info.color }}
                    aria-hidden="true"
                  />
                  {step.status_info.name}
                </span>
              )}
              {/* due date — inline editable via a native date picker overlaid on
                  the cell, mirroring the task-row pattern in RecordRow. */}
              {!linked && (step.due_date || !readOnly) && (
                <span class="relative inline-flex items-center shrink-0" title="Fälligkeit">
                  {step.due_date ? (
                    <span
                      class={`text-micro tabular-nums ${
                        !step.is_done && step.due_date < localTodayIso()
                          ? "text-[color:var(--color-prio-urgent)] font-semibold"
                          : "text-[color:var(--color-on-surface-variant)]"
                      }`}
                    >
                      {formatDateShort(step.due_date)}
                    </span>
                  ) : (
                    <CalendarPlus
                      size={14}
                      class="text-[color:var(--color-outline)] opacity-100 md:opacity-40 md:group-hover/step:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  )}
                  {!readOnly && (
                    <input
                      type="date"
                      value={step.due_date ?? ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        const el = e.currentTarget as HTMLInputElement & {
                          showPicker?: () => void;
                        };
                        try {
                          el.showPicker?.();
                        } catch {
                          /* showPicker may throw without a user gesture — the
                             native input still opens on focus/click. */
                        }
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        const v = (e.currentTarget as HTMLInputElement).value;
                        void setDue(step, v || null);
                      }}
                      class="absolute inset-0 opacity-0 cursor-pointer"
                      aria-label={`Fälligkeit von „${step.title}"`}
                    />
                  )}
                </span>
              )}
              {/* In eine eigene Aufgabe umwandeln — nur für echte Schritte:
                  ein Referenz-Schritt zeigt bereits auf eine Aufgabe. */}
              {!readOnly && !linked && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    void promote(step);
                  }}
                  class="shrink-0 inline-flex size-7 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] opacity-100 md:opacity-0 md:group-hover/step:opacity-100 hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-primary)] focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-50"
                  aria-label={`Schritt „${step.title}" in eine Aufgabe umwandeln`}
                  title="In eine eigene Aufgabe umwandeln (verlässt diese Checkliste)"
                >
                  <ArrowUpRight size={14} aria-hidden="true" />
                </button>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void remove(step);
                  }}
                  class="shrink-0 inline-flex size-7 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] opacity-100 md:opacity-0 md:group-hover/step:opacity-100 hover:bg-[color:var(--color-error)]/10 hover:text-[color:var(--color-error)] focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)]"
                  aria-label={
                    linked
                      ? `Verknüpfung „${step.title}" entfernen`
                      : `Schritt „${step.title}" löschen`
                  }
                  title={linked ? "Verknüpfung entfernen (Aufgabe bleibt bestehen)" : "Schritt löschen"}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </li>
            );
          })}
          {!readOnly && !linkMode && (
            <li class="flex items-center gap-2 px-1 py-1">
              {/* Real submit button (not just decoration) so touch users can
                  add without relying on the Enter key. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void add();
                }}
                disabled={busy || title.trim() === ""}
                class="shrink-0 inline-flex size-6 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] enabled:hover:bg-[color:var(--color-surface-container)] enabled:hover:text-[color:var(--color-primary)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                aria-label="Schritt hinzufügen"
                title="Schritt hinzufügen"
              >
                <Plus size={14} aria-hidden="true" />
              </button>
              <input
                type="text"
                value={title}
                onClick={(e) => e.stopPropagation()}
                onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void add();
                  }
                }}
                placeholder="Schritt hinzufügen…"
                aria-label="Neuen Schritt hinzufügen"
                maxLength={500}
                disabled={busy}
                class="w-full min-w-0 bg-transparent text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none disabled:opacity-60"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLinkMode(true);
                }}
                class="shrink-0 inline-flex size-6 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                aria-label="Bestehende Aufgabe verknüpfen"
                title="Bestehende Aufgabe als Schritt verknüpfen"
              >
                <Link2 size={14} aria-hidden="true" />
              </button>
              {/* #826: offene Punkte in eine neue Aufgabe übernehmen. Der
                  Dialog ist global gemountet (App.tsx) — hier fällt nur der
                  Auslöser an, damit die Detailansicht denselben nutzen kann. */}
              {steps.some((s) => !s.is_done) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    carryOverStepsSig.value = taskId;
                  }}
                  class="shrink-0 inline-flex size-6 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                  aria-label="Offene Punkte übernehmen"
                  title="Offene Punkte in eine neue Aufgabe übernehmen und diese abschliessen"
                >
                  <ArrowRightToLine size={14} aria-hidden="true" />
                </button>
              )}
            </li>
          )}
          {!readOnly && linkMode && (
            <li class="flex flex-col gap-1 px-1 py-1">
              <div class="flex items-center gap-2">
                <Link2 size={14} class="shrink-0 text-[color:var(--color-primary)]" aria-hidden="true" />
                <input
                  type="text"
                  value={linkQuery}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onInput={(e) => setLinkQuery((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setLinkMode(false);
                      setLinkQuery("");
                      setLinkResults(null);
                    }
                  }}
                  placeholder="Aufgabe suchen und verknüpfen…"
                  aria-label="Aufgabe suchen und als Schritt verknüpfen"
                  maxLength={100}
                  disabled={busy}
                  class="w-full min-w-0 bg-transparent text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkMode(false);
                    setLinkQuery("");
                    setLinkResults(null);
                  }}
                  class="shrink-0 text-micro text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] focus:outline-none"
                  aria-label="Verknüpfen abbrechen"
                >
                  Abbrechen
                </button>
              </div>
              {linkResults !== null && (
                <ul class="rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-1 overflow-hidden">
                  {linkResults.length === 0 ? (
                    <li class="px-2 py-1.5 text-xs italic text-[color:var(--color-on-surface-variant)]">
                      Keine passende Aufgabe gefunden
                    </li>
                  ) : (
                    linkResults.map((r) => (
                      <li key={`${r.type}:${r.id}`}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            void link(r);
                          }}
                          class="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus:bg-[color:var(--color-surface-container-low)] disabled:opacity-50"
                        >
                          <Link2 size={14} class="shrink-0 text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
                          <span class="min-w-0 truncate text-[color:var(--color-on-surface)]">{r.title}</span>
                          <span class="ml-auto shrink-0 text-label text-[color:var(--color-on-surface-variant)]">
                            {r.type === "task" ? (r.subtitle ?? "Projektaufgabe") : "Persönliche Aufgabe"}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </li>
          )}
          {readOnly && steps.length === 0 && (
            <li class="py-1 text-xs italic text-[color:var(--color-on-surface-variant)]">Keine Schritte</li>
          )}
        </ul>
      )}
    </div>
  );
}
